/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Check, AlertCircle } from 'lucide-react';
import { passwordResetService } from '../lib/supabase';
import Dompurify from 'dompurify';
import { validateEmail } from '../utils/validations';

// Ensure all user input is sanitized to prevent XSS attacks
const sanitizeInput = (input: string) => {
  return Dompurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

const PasswordResetRequestPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate email format
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        throw new Error(emailValidation.error);
      }
      const sanitizedEmail = sanitizeInput(email.trim());
      
      if (!sanitizedEmail) {
        throw new Error('Please enter a valid email address');
      }

      await passwordResetService.requestPasswordReset(sanitizedEmail);
      setSubmitted(true);
    } catch (error: any) {
      console.error('Error requesting password reset:', error);
      
      // Security: Don't reveal if email exists or not
      if (error?.message?.includes('User not found') || error?.message?.includes('Invalid email')) {
        setError('If this email is registered with us, you will receive a password reset link shortly.');
      } else if (error?.message?.includes('Email rate limit exceeded')) {
        setError('Too many password reset requests. Please wait before trying again.');
      } else {
        setError('If this email is registered with us, you will receive a password reset link shortly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-white/50"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <Check className="w-8 h-8 text-white" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-charcoal mb-4">Check Your Email</h2>
          <p className="text-slate-gray mb-6">
            If an account with that email exists, we've sent you a password reset link. 
            Please check your email and follow the instructions to reset your password.
          </p>
          <p className="text-sm text-slate-gray mb-6">
            Didn't receive the email? Check your spam folder or try again in a few minutes.
          </p>
          
          <div className="space-y-3">
            <Link to="/startup-signup">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Back to Sign In
              </motion.button>
            </Link>
            
            <button
              onClick={() => {
                setSubmitted(false);
                setEmail('');
                setError(null);
              }}
              className="w-full px-6 py-3 border border-electric-blue text-electric-blue font-semibold rounded-xl hover:bg-electric-blue/5 transition-all duration-300"
            >
              Try Different Email
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-lime-green/20 to-soft-teal/15 backdrop-blur-sm border-b border-lime-green/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Link to="/startup-signup" className="inline-flex items-center text-electric-blue hover:text-deep-indigo transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sign In
          </Link>
          <div className="flex items-center flex-wrap">
            <div className="w-10 h-10 bg-gradient-to-r from-lime-green to-soft-teal rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg">
              <Mail className="w-6 h-6 text-white" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-charcoal">Reset Your Password</h1>
              <p className="text-sm sm:text-base text-slate-gray">
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-md mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={handleSubmit}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/50"
        >
          <div className="p-6 sm:p-8">
            {/* Email Input */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="mb-6"
            >
              <label htmlFor="email" className="block text-sm sm:text-base font-medium text-charcoal mb-2">
                <Mail className="inline w-4 h-4 mr-1" />
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 sm:py-4 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue focus:border-transparent transition-all duration-300 bg-white/80 text-base"
                placeholder="Enter your email address"
                id="email"
                disabled={isSubmitting}
              />
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl"
                aria-live="polite"
                role="alert"
              >
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-6 sm:px-8 py-4 sm:py-5 bg-gradient-success text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-base sm:text-lg min-h-[48px]"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending Reset Link...
                </div>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2 inline" aria-hidden="true" />
                  Send Reset Link
                </>
              )}
            </motion.button>

            {/* Additional Help */}
            <div className="text-center mt-6 pt-6 border-t border-slate-gray/20">
              <p className="text-sm text-slate-gray mb-2">
                Remember your password?
              </p>
              <Link
                to="/startup-signup"
                className="text-sm text-electric-blue hover:text-deep-indigo transition-colors font-medium"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default PasswordResetRequestPage; 